import React, { useState } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  FileText,
  Search,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Download,
  Calendar,
} from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

// Helper function to get status config with translations
const getStatusConfig = (t) => ({
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("pending") },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: t("processing") },
  completed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("completed") },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("failed") },
});

export default function MerchantSettlements() {
  const { t } = useLanguage();
  const statusConfig = getStatusConfig(t);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  // TODO: Implement custom API calls
  const { data: settlements = [], isLoading } = useQuery({
    queryKey: ["settlements"],
    queryFn: async () => [], // TODO: Implement Settlement.list API call
  });

  const filteredSettlements = settlements.filter((s) => {
    if (search && !s.settlement_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    return true;
  });

  const paginatedSettlements = filteredSettlements.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredSettlements.length / pageSize);

  // Summary stats
  const totalSettled = settlements.filter(s => s.status === "completed").reduce((sum, s) => sum + (s.net_amount || 0), 0);
  const pendingAmount = settlements.filter(s => s.status === "pending" || s.status === "processing").reduce((sum, s) => sum + (s.net_amount || 0), 0);

  return (
    <MerchantLayout currentPage="MerchantSettlements" merchantName="Royal Gaming" merchantCode="MRC001">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("settlementTitle")}</h1>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-5 border border-slate-100"
          >
            <p className="text-sm text-slate-500 mb-1">{t("totalSettled")}</p>
            <p className="text-2xl font-bold text-emerald-600">฿{totalSettled.toLocaleString()}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-xl p-5 border border-slate-100"
          >
            <p className="text-sm text-slate-500 mb-1">{t("pendingSettlement")}</p>
            <p className="text-2xl font-bold text-amber-600">฿{pendingAmount.toLocaleString()}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-5 border border-slate-100"
          >
            <p className="text-sm text-slate-500 mb-1">{t("totalRecords")}</p>
            <p className="text-2xl font-bold text-slate-900">{settlements.length}</p>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-100 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("searchBySettlementId")}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t("status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatus")}</SelectItem>
              <SelectItem value="completed">{t("completed")}</SelectItem>
              <SelectItem value="pending">{t("pending")}</SelectItem>
              <SelectItem value="processing">{t("processing")}</SelectItem>
              <SelectItem value="failed">{t("failed")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("settlementId")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("period")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("transactions")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("deposits")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("withdrawals")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("totalFees")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("netAmount")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td colSpan={8} className="px-4 py-4">
                        <div className="h-4 bg-slate-100 rounded animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : paginatedSettlements.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>{t("noSettlements")}</p>
                    </td>
                  </tr>
                ) : (
                  paginatedSettlements.map((settlement, index) => {
                    const status = statusConfig[settlement.status] || statusConfig.pending;
                    const StatusIcon = status.icon;

                    return (
                      <motion.tr
                        key={settlement.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="border-b border-slate-50 hover:bg-slate-50"
                      >
                        <td className="px-4 py-3">
                          <p className="font-mono text-sm text-slate-900">{settlement.settlement_id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Calendar className="h-3 w-3" />
                            {settlement.period_start ? format(new Date(settlement.period_start), "MMM d") : "-"}
                            {settlement.period_end && ` - ${format(new Date(settlement.period_end), "MMM d")}`}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">{settlement.transaction_count || 0}</td>
                        <td className="px-4 py-3 text-sm text-blue-600 font-medium">฿{(settlement.total_deposits || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-purple-600 font-medium">฿{(settlement.total_withdrawals || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">฿{(settlement.total_fees || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm font-bold text-emerald-600">฿{(settlement.net_amount || 0).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <Badge className={status.color}>
                            <StatusIcon className={`h-3 w-3 mr-1 ${settlement.status === "processing" ? "animate-spin" : ""}`} />
                            {status.label}
                          </Badge>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, filteredSettlements.length)} {t("of")} {filteredSettlements.length}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </MerchantLayout>
  );
}